import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,   
  CircularProgress, 
  Typography, 
  Tooltip, 
  IconButton, 
} from '@mui/material';
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Todos = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "Todos", "create", false);
  const canEdit   = hasPermission(user?.permissions, "Todos", "update", false);
  const canDelete = hasPermission(user?.permissions, "Todos", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [todos, setTodos] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const handleTodos = async () => {
      try {
        setError(null);
        setLoading(true);  
        const res = await api.get('/todos');  
        setTodos(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    handleTodos();
  }, []); 


  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "todo", 
      headerName: "Todo", 
      ...(() => {
        return isMobile ? { width: 200 } : { flex: 2 };
      })(),
    },
    {
      field: "description",
      headerName: "Description",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 4 };
      })(),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 90,      
      renderCell: (params) => {
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >           
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}                
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/todos/${params.row._id}`)
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForever />
              </IconButton>                
            </Tooltip>            
          </Box>
        );
      },
    },
  ];
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, todos);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/todos/delete/${idToDelete}`);

        setTodos(todos.filter((item) => item._id !== idToDelete));
        toast.success('To do deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, todos);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/todos/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setTodos(todos.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete to dos!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    todos,
  ]);
 

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="To dos" 
          subtitle={`${loading ? 'Loading...' : 'List of To dos'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, todos)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/todos/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
          <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
          </FlexCenter>
      ) : error ? (
          <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">Error: {error?.message || ''}</Typography>
          </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12" }}    
          >                
            {isMobile ? (
              <CardList
                rows={todos}
                primaryField="todo"
                fields={[
                  { label: "Description", key: "description" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/todos/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={todos || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Todos;
